<script setup>
import {onMounted, onUnmounted, ref} from 'vue';
import LoadingComponent from "@/components/utils/LoadingComponent.vue";
import { trackScreenSize } from "@/globals/utils/getScreenSize";
import { formatDate } from "@/globals/utils/formatDate";
import {customToast} from "@/globals/utils/customToast";
import {cancelRide} from "@/services/tripService";
import MailTo from "@/components/utils/MailTo.vue";
import PhoneCall from "@/components/utils/PhoneCall.vue";
import ConfirmModal from "@/components/utils/ConfirmModal.vue";
import socket from "@/services/socket";
import {useRoute} from "vue-router";
import router from "@/router";
import {useRideStore} from "@/stores/ride";
import PaginationComponent from "@/components/utils/PaginationComponent.vue";
import FloatingButtonAction from "@/components/utils/FloatingButtonAction.vue";

const rideStore = useRideStore();

const currentPage = ref(Number(router.currentRoute.value.query.page) || 1);

const trips = ref([]);
const loading = ref(true);
const error = ref(null);
// const searchQuery = ref('');
const screenInfo = ref({ width: 0, height: 0, deviceType: "DESKTOP" });
let removeListener;

// Variables reactivas
const route = useRoute();
const statusList = ref(['all', 'pending', 'accepted', 'in_your_location', 'in_progress', 'completed', 'cancelled', 'reserved']);
const status = ref(route.params.status || 'all');

console.log('status', status.value);

// Modal constants
const showConfirmModal = ref(false)
const modalTitle = ref('Eliminar elemento')
const modalMessage = ref('¿Estás seguro que deseas eliminar este elemento? Esta acción no se puede deshacer.')
const selectedTrip = ref(null)

// Obtener la lista de viajes al montar el componente
const fetchTrips = async () => {
  loading.value = true;
  try {
    // Obtener los viajes según el estado
    if(status.value === 'all'){
      trips.value = await rideStore.userrides('',currentPage.value);
    }else{
      trips.value = await rideStore.userrides(status.value, currentPage.value);
    }
  } catch (err) {
    error.value = 'Error al cargar los viajes.';
    console.error(err);
  } finally {
    loading.value = false;
  }
};

// handleCancelConfirm
const handleConfirm = async () => {
  showConfirmModal.value = false
  try {
    // Lógica de cancelación
    await cancelRide(selectedTrip.value._id);
    // Cambiar el estado del viaje a cancelado
    selectedTrip.value.status = 'cancelled';
    customToast('Viaje cancelado correctamente.', 'success');
  }
  catch (err) {
    customToast('Error al cancelar el viaje.', 'error');
  }
}

// handleCancelCancel
const handleCancel = () => {
  // Lógica de cancelación
  showConfirmModal.value = false
}

// Por ejemplo, para abrir el modal:
const openModal = (trip) => {
  selectedTrip.value = trip
  modalTitle.value = 'Cancel trip'
  modalMessage.value = `Are you sure you want to cancel the trip from ${trip.pickup.address} to ${trip.destination.address}?`
  console.log('selectedTrip', selectedTrip.value)
  showConfirmModal.value = true
}

// Al montar el componente
onMounted(() => {
  // Obtener la lista de viajes
  fetchTrips();
  // Obtener el tamaño de la pantalla
  removeListener = trackScreenSize((info) => {
    screenInfo.value = info;
  });

  // Conectar al socket
  socket.connect();

  // Escuchar un evento específico
  socket.on("newRide", async () => {
    await fetchTrips();
    customToast('New ride created', 'success');
  });

  // Escuchar cambios en el estado de los rides
  socket.on("rideStatusUpdated", async (data) => {
    await fetchTrips();
    customToast(`Trip ${data.status}`, 'success');
  });

  socket.on("rideAccepted" , async () => {
    await fetchTrips();
    customToast('Trip accepted', 'success');
  });

  console.log("socket", socket.connected);

});

// Update page and fetch drivers
const handlePageChange = async (page) => {
  currentPage.value = page;
  await fetchTrips();
};

// Al desmontar el componente
onUnmounted(() => {
  // Remover el listener al desmontar el componente
  if (removeListener) removeListener();
  // Desconectar el socket y limpiar eventos
  socket.off("newRide");
  socket.off("rideAccepted");
  socket.off("rideStatusUpdated");
  socket.off("rideCancelled");
  socket.disconnect();
});

// cambiar la direcion de la pagina al cambiar el filtro
const changeStatus = async () => {
  if (status.value === 'all') {
    await router.push({name: 'Trips'});
  } else {
    await router.push({name: 'TripsByStatus', params: {status: status.value}});
  }
  await fetchTrips();
};

</script>

<template>
  <FloatingButtonAction path="/user/trips/create">
    <span class="material-icons mr-2">add_circle</span> Create trip
  </FloatingButtonAction>
  <div class="flex justify-between items-center mb-4">
    <div class="flex items-center">
      <label for="status" class="block text-gray-700 dark:text-gray-300 font-medium mr-2">Filter by status:</label>
      <select
        id="status"
        v-model="status"
        @change="changeStatus"
        class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-200"
      >
        <option v-for="item in statusList" :key="item" :value="item">{{ item }}</option>
      </select>
    </div>
  </div>

  <ConfirmModal
    :show="showConfirmModal"
    :title="modalTitle"
    :message="modalMessage"
    :onConfirm="handleConfirm"
    :onCancel="handleCancel"
  />

  <LoadingComponent v-if="loading" />
  <div v-else>
    <div class="overflow-x-auto bg-white dark:bg-gray-700 rounded-lg shadow">
      <table class="min-w-full table-auto" v-if="trips.data.length > 0">
        <thead class="bg-gray-100 dark:bg-gray-700 pb-3">
          <tr>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200" v-if="screenInfo.deviceType === 'DESKTOP'">Client</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200" v-if="screenInfo.deviceType === 'DESKTOP'">Driver</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Origin</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Destination</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Status</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200" v-if="screenInfo.deviceType === 'DESKTOP'">Date</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200" v-if="screenInfo.deviceType === 'DESKTOP'">Time</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="trip in trips.data" :key="trip._id" class="odd:bg-gray-50 even:bg-gray-100 hover:bg-gray-200 dark:odd:bg-gray-700 dark:even:bg-gray-600 dark:hover:bg-gray-500">
            <!-- Client -->
            <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200" v-if="screenInfo.deviceType === 'DESKTOP'">
              <div v-if="trip.user">
                <strong>{{ trip.user.name }}</strong><br>
                <MailTo v-if="trip.user.email" :email="trip.user.email" /><br>
                <PhoneCall v-if="trip.user.phone" :phone="trip.user.phone" />
              </div>
              <div v-else>
                <span class="text-gray-500 dark:text-gray-400 italic">No user data</span>
              </div>
            </td>

            <!-- Driver -->
            <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200" v-if="screenInfo.deviceType === 'DESKTOP'">
              <div v-if="trip.driver">
                <strong>{{ trip.driver.name }}</strong><br>
                <MailTo v-if="trip.driver.email" :email="trip.driver.email" /><br>
                <PhoneCall v-if="trip.driver.phone" :phone="trip.driver.phone" />
              </div>
              <div v-else>
                <span class="text-gray-500 dark:text-gray-400 italic">
                   <span class="material-icons" style="font-size:16px;">info</span> No driver assigned
                </span>
              </div>
            </td>

            <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">{{ trip.pickup?.address ?? 'Unknown' }}</td>
            <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">{{ trip.destination?.address ?? 'Unknown' }}</td>
            <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
              <div
                :class="{
                  'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200': trip.status === 'cancelled',
                  'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200': trip.status === 'accepted',
                  'bg-yellow-200 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200': trip.status === 'pending',
                  'bg-pink-200 text-pink-800 dark:bg-pink-900 dark:text-pink-200': trip.status === 'in_your_location',
                  'bg-gray-200 text-gray-800 dark:bg-gray-900 dark:text-gray-200': trip.status === 'in_progress',
                  'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200': trip.status === 'completed',
                  'bg-teal-200 text-teal-800 dark:bg-teal-900 dark:text-teal-200': trip.status === 'reserved',
                }"
                class="px-2 py-1 rounded-md text-center font-semibold"
              >
                {{ trip.status }}
              </div>
              <div v-if="trip.status === 'reserved'" class="bg-teal-200 text-teal-800 dark:bg-teal-900 dark:text-teal-200 text-sm p-1 rounded shadow-sm mt-2 font-semibold">
                <span class="material-icons" style="font-size:15px;">calendar</span> {{ formatDate(trip.bookingDate, "DD/MM/YYYY") }} <br>
                <span class="material-clock" style="font-size:15px;" >calendar</span> {{ formatDate(trip.bookingDate, "HH:mm A") }}
              </div>
            </td>
            <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200" v-if="screenInfo.deviceType === 'DESKTOP'">{{ formatDate(trip.createdAt, "DD/MM/YYYY") }}</td>
            <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200" v-if="screenInfo.deviceType === 'DESKTOP'">{{ formatDate(trip.createdAt, "HH:mm") }}</td>
            <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
              <div class="flex space-x-2">
                <!-- Info -->
                <button
                  title="Info"
                  @click="() => router.push({name: 'UserTripDetails', params: {id: trip._id}})"
                  class="flex items-center justify-center px-2 py-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-white font-medium rounded-md shadow transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400"
                >
                  <span class="material-icons-round" style="font-size: 16px;">info</span>
                </button>
                <!-- Cancel -->
                <button
                  v-if="trip.status === 'pending' && trip.status !== 'completed' && trip.status !== 'cancelled'"
                  @click="openModal(trip)"
                  title="Cancel"
                  class="flex items-center justify-center px-2 py-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-white font-medium rounded-md shadow transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400"
                >
                  <span class="material-icons-round" style="font-size: 16px;">cancel</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="text-center text-gray-500 dark:text-gray-400 p-5 rounded shadow">
        <span class="material-icons" style="font-size:15px;"></span> Without trips
      </div>
      <PaginationComponent
        :currentPage="currentPage"
        :totalPages="trips.totalPages"
        :onChangePage="handlePageChange"
        class="mb-3"
      />
    </div>
  </div>
</template>

<style scoped>
/* Estilo adicional */
</style>
